"use client";

import { clearUserStoreToken, getUserStoreToken, setUserStoreToken } from "@/lib/stores/user-store";

const FETCH_PATCH_FLAG = "__portalFetchWithAuthPatched";
const IMPERSONATION_SOURCE_TOKEN_KEY = "portal.impersonation.sourceToken";

function isBrowser() {
  return typeof window !== "undefined";
}

export function getAuthToken() {
  if (!isBrowser()) return null;
  return getUserStoreToken();
}

export function setAuthToken(token: string) {
  if (!isBrowser()) return;
  setUserStoreToken(token);
}

export function clearAuthToken() {
  if (!isBrowser()) return;
  window.sessionStorage.removeItem(IMPERSONATION_SOURCE_TOKEN_KEY);
  clearUserStoreToken();
}

export function isImpersonating() {
  if (!isBrowser()) return false;
  return !!window.sessionStorage.getItem(IMPERSONATION_SOURCE_TOKEN_KEY);
}

export function startImpersonation(nextToken: string) {
  if (!isBrowser()) return false;
  const currentToken = getAuthToken();
  if (!currentToken || !nextToken.trim()) return false;

  if (!isImpersonating()) {
    window.sessionStorage.setItem(IMPERSONATION_SOURCE_TOKEN_KEY, currentToken);
  }

  setAuthToken(nextToken);
  return true;
}

export function stopImpersonation() {
  if (!isBrowser()) return false;
  const sourceToken = window.sessionStorage.getItem(IMPERSONATION_SOURCE_TOKEN_KEY);
  if (!sourceToken) return false;
  window.sessionStorage.removeItem(IMPERSONATION_SOURCE_TOKEN_KEY);
  setAuthToken(sourceToken);
  return true;
}

function shouldAttachAuthHeader(input: RequestInfo | URL) {
  if (!isBrowser()) return false;

  if (input instanceof Request) {
    return shouldAttachAuthHeader(input.url);
  }

  const raw = typeof input === "string" ? input : input.toString();
  if (raw.startsWith("/api")) return true;

  try {
    const url = new URL(raw, window.location.origin);
    return url.origin === window.location.origin && url.pathname.startsWith("/api");
  } catch {
    return false;
  }
}

export function installAuthFetchInterceptor() {
  if (!isBrowser()) return;

  const markerWindow = window as Window & { [FETCH_PATCH_FLAG]?: boolean };
  if (markerWindow[FETCH_PATCH_FLAG]) {
    return;
  }

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const token = getAuthToken();
    if (!token || !shouldAttachAuthHeader(input)) {
      return originalFetch(input, init);
    }

    const baseHeaders =
      input instanceof Request
        ? input.headers
        : undefined;
    const headers = new Headers(init?.headers ?? baseHeaders);
    if (!headers.has("authorization") && !headers.has("x-api-token")) {
      headers.set("authorization", `Bearer ${token}`);
    }

    if (input instanceof Request) {
      return originalFetch(new Request(input, { ...init, headers }));
    }

    return originalFetch(input, { ...init, headers });
  };

  markerWindow[FETCH_PATCH_FLAG] = true;
}
